// GridSelect.tsx
import { FC, useState, useMemo, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Grid, Box } from '@mui/material'; 
import Colors from '../global/Colors';
import LabelRow from './LabelRow';

const SQUARE_SIZE = 10.8; // Size in pixels for each square
const GRID_GAP = 2; // Gap between grid squares in pixels

export interface GridSelection {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
}

export interface GridSelectProps {
  rows: number;
  cols: number;
  onSelect: (selection: GridSelection) => void;
  colorMode?: 'light' | 'dark';
  name?: string;
  initialSelection?: GridSelection | null;
  actualCount?: number;
  autoFit?: boolean;
  disabled?: boolean;
}

const DEFAULT_FILL_COLOR = (colorMode: 'light' | 'dark') =>
  colorMode === 'dark' ? Colors.grey9 : Colors.background;

const DEFAULT_BORDER_COLOR = (colorMode: 'light' | 'dark') =>
  colorMode === 'dark' ? Colors.grey8 : Colors.grey1;
  
const SELECTED_FILL_COLOR = (colorMode: 'light' | 'dark') =>
  colorMode === 'dark' ? Colors.grey8 : Colors.grey2;

const SELECTED_BORDER_COLOR = (colorMode: 'light' | 'dark') =>
  colorMode === 'dark' ? Colors.grey8 : Colors.grey3;
  
const HOVER_BORDER_COLOR = (colorMode: 'light' | 'dark') =>
  colorMode === 'dark' ? Colors.grey2 : Colors.grey8;

interface StyledGridContainerProps {
  $colorMode: 'light' | 'dark';
  $width: number;
  $disabled?: boolean;
}

const StyledGridContainer = styled(Box)<StyledGridContainerProps>`
  display: inline-flex;
  flex-direction: column;
  position: relative; 
  width: ${(props) => props.$width}px;
  gap: 2px;
  ${(props) => props.$disabled && css`
    opacity: 0.8;
    // filter: grayscale(0.5);
    pointer-events: none;
  `}
`;

interface StyledGridCellProps {
  $colorMode: 'light' | 'dark';
  $isHovered: boolean; 
  $isSelected: boolean; 
  $isFilled: boolean;
  $isStart: boolean;
  $isEnd: boolean;
}

const StyledGridCell = styled(Box)<StyledGridCellProps>`
  width: ${SQUARE_SIZE}px;
  height: ${SQUARE_SIZE}px;
  box-sizing: border-box;
  cursor: pointer;
  border: 1px solid ${(props: StyledGridCellProps) => DEFAULT_BORDER_COLOR(props.$colorMode)};
  border-radius: 2px;
  background-color: ${(props: StyledGridCellProps) => DEFAULT_FILL_COLOR(props.$colorMode)}; 
  margin: ${GRID_GAP / 2}px;
  transition: background-color 0.1s ease;

  /* Sequential fill based on actualCount */
  ${(props: StyledGridCellProps) => props.$isFilled && css`
    background-color: ${SELECTED_FILL_COLOR(props.$colorMode)};
  `}

  /* The Selection Area (indicated by border) */
  ${(props: StyledGridCellProps) => props.$isSelected && css`
    border-color: ${SELECTED_BORDER_COLOR(props.$colorMode)};
  `}

  /* Current Selection/Hover*/
  ${(props: StyledGridCellProps) => props.$isHovered && css`
    border-color: ${HOVER_BORDER_COLOR(props.$colorMode)};
  `}

  /* If a cell is both hovered and selected, the hovered border should take precedence/be maintained */
  ${(props: StyledGridCellProps) => props.$isHovered && props.$isSelected && css`
    border-color: ${HOVER_BORDER_COLOR(props.$colorMode)};
  `}
`;

const isCellInSelection = (
  row: number,
  col: number,
  selection: GridSelection | null,
): boolean => {
  if (!selection) return false;
  
  // The selection is always anchored at (0, 0)
  return (
    row <= selection.endRow &&
    col <= selection.endCol
  );
};

const getSelectionSize = (selection: GridSelection | null): { rows: number; cols: number } | null => {
  if (!selection) return null;

  const rowCount = selection.endRow + 1; 
  const colCount = selection.endCol + 1; 
  
  return { rows: rowCount, cols: colCount };
};

const GridSelect: FC<GridSelectProps> = ({
  rows,
  cols,
  onSelect,
  colorMode = 'dark',
  name = 'Frames', 
  initialSelection = null,
  actualCount = 0,
  autoFit = false,
  disabled = false,
}) => {
  const [internalConfirmedSelection, setInternalConfirmedSelection] = useState<GridSelection | null>(initialSelection);
  
  const [dragStart, setDragStart] = useState<[number, number] | null>(null);
  const [dragEnd, setDragEnd] = useState<[number, number] | null>(null);
  
  const [hoveredCell, setHoveredCell] = useState<[number, number] | null>(null);

  // Sync internal state with prop if it changes and we are not dragging
  useEffect(() => {
    if (!dragStart) {
        const currentSelectionString = JSON.stringify(internalConfirmedSelection);
        const initialSelectionString = JSON.stringify(initialSelection);

        if (currentSelectionString !== initialSelectionString) {
            setInternalConfirmedSelection(initialSelection);
        }
    }
  }, [initialSelection, dragStart]);

  const gridWidth = useMemo(() => {
    return cols * SQUARE_SIZE + cols * GRID_GAP;
  }, [cols]);

  const getAutoFitSelection = useCallback((targetRow: number, targetCol: number): GridSelection => {
    if (!autoFit || actualCount <= 0) {
      return { startRow: 0, startCol: 0, endRow: targetRow, endCol: targetCol };
    }

    const targetCols = targetCol + 1;
    const requiredRows = Math.ceil(actualCount / targetCols);
    
    return {
      startRow: 0,
      startCol: 0,
      endRow: Math.max(0, requiredRows - 1),
      endCol: targetCol,
    };
  }, [autoFit, actualCount]);

  const visualSelection: GridSelection | null = useMemo(() => {
    if (disabled) return null;
    if (dragStart && dragEnd) {
      return getAutoFitSelection(dragEnd[0], dragEnd[1]);
    } 
    else if (hoveredCell) {
      return getAutoFitSelection(hoveredCell[0], hoveredCell[1]);
    }
    return null;

  }, [dragStart, dragEnd, hoveredCell, getAutoFitSelection, disabled]); 

  const currentSelection: GridSelection | null = useMemo(() => {
    if (disabled || !dragStart || !dragEnd) return null;
    return getAutoFitSelection(dragEnd[0], dragEnd[1]);
  }, [dragStart, dragEnd, getAutoFitSelection, disabled]); 

  const displaySelection = visualSelection || internalConfirmedSelection;
  
  const selectionSize = useMemo(() => getSelectionSize(displaySelection), [displaySelection]);
  
  const handleMouseDown = useCallback((row: number, col: number) => {
    if (disabled) return;
    setDragStart([0, 0]); 
    setDragEnd([row, col]);
    setInternalConfirmedSelection(null); 
  }, [disabled]);

  const handleMouseEnter = useCallback((row: number, col: number) => {
    if (disabled) return;
    setHoveredCell([row, col]); 
    if (dragStart) {
      setDragEnd([row, col]);
    }
  }, [dragStart, disabled]);
  
  const handleMouseLeaveGrid = useCallback(() => {
    setHoveredCell(null); 
    
    if (dragStart) { 
      if (currentSelection) {
        setInternalConfirmedSelection(currentSelection);
        onSelect(currentSelection);
      }
      setDragStart(null);
      setDragEnd(null);
    }
  }, [dragStart, currentSelection, onSelect]); 
  
  const handleMouseUp = useCallback(() => {
    if (currentSelection) {
      setInternalConfirmedSelection(currentSelection);
      onSelect(currentSelection);
    }
    setDragStart(null);
    setDragEnd(null);
  }, [currentSelection, onSelect]);

  const gridCells = useMemo(() => {
    const cells = [];

    for (let r = 0; r < rows; r++) {
      const rowCells = [];
      for (let c = 0; c < cols; c++) {
        
        const isHovered = isCellInSelection(r, c, visualSelection); 
        
        // Only show internal selection if we are NOT currently hovering or dragging
        const isSelected = !visualSelection && isCellInSelection(r, c, internalConfirmedSelection);

        let isFilled = false;
        if (displaySelection) {
            const gridWidth = displaySelection.endCol + 1;
            const index = (r * gridWidth) + c;
            if (c <= displaySelection.endCol && r <= displaySelection.endRow) {
                isFilled = index < actualCount;
            }
        }
        
        const isStart = (r === 0 && c === 0) && (displaySelection !== null);
          
        const isEnd = displaySelection && (r === displaySelection.endRow && c === displaySelection.endCol);

        rowCells.push(
          <StyledGridCell
            key={`${r}-${c}`}
            $colorMode={colorMode}
            $isHovered={isHovered}
            $isSelected={isSelected}
            $isFilled={isFilled}
            $isStart={isStart}
            $isEnd={isEnd}
            onMouseDown={() => handleMouseDown(r, c)}
            onMouseEnter={() => handleMouseEnter(r, c)}
          />
        );
      }
      cells.push(
        <Grid 
          container 
          item 
          key={r} 
          style={{ width: 'fit-content' }}
        >
          {rowCells}
        </Grid>
      );
    }
    return cells;
  }, [rows, cols, colorMode, visualSelection, internalConfirmedSelection, displaySelection, actualCount, handleMouseDown, handleMouseEnter]); 

  const sizeValue = selectionSize 
    ? `${selectionSize.rows} x ${selectionSize.cols}` 
    : undefined;
    
  return (
    <StyledGridContainer 
      $colorMode={colorMode} 
      $width={gridWidth}
      $disabled={disabled}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeaveGrid}
    >

      <LabelRow 
        label={name} 
        value={sizeValue} 
        size="mini"
      />
      
      <Grid container direction="column">
        {gridCells}
      </Grid>
      
    </StyledGridContainer>
  );
};

export default GridSelect;